/**
 * Utility per il codice fiscale italiano (persone fisiche).
 * Gestisce carattere di controllo, omocodia e data di nascita.
 */

const CF_REGEX =
  /^[A-Z]{6}[0-9LMNPQRSTUV]{2}[ABCDEHLMPRST][0-9LMNPQRSTUV]{2}[A-Z][0-9LMNPQRSTUV]{3}[A-Z]$/;

const MONTH_LETTERS = 'ABCDEHLMPRST';
const OMOCODIA_LETTERS = 'LMNPQRSTUV';
const OMOCODIA_POSITIONS = [6, 7, 9, 10, 12, 13, 14];

// Valori dei caratteri in posizione dispari (1ª, 3ª, ...) per A..Z / 0..9
const ODD_VALUES = [
  1, 0, 5, 7, 9, 13, 15, 17, 19, 21, 2, 4, 18, 20, 11, 3, 6, 8, 12, 14, 16, 10,
  22, 25, 24, 23,
];

const normalizeCodiceFiscale = (value) =>
  String(value || '')
    .replace(/\s+/g, '')
    .toUpperCase();

const charIndex = (ch) =>
  ch >= '0' && ch <= '9' ? ch.charCodeAt(0) - 48 : ch.charCodeAt(0) - 65;

const computeCheckChar = (first15) => {
  let sum = 0;
  for (let i = 0; i < 15; i += 1) {
    const idx = charIndex(first15[i]);
    sum += i % 2 === 0 ? ODD_VALUES[idx] : idx;
  }
  return String.fromCharCode(65 + (sum % 26));
};

const isValidCodiceFiscale = (value) => {
  const cf = normalizeCodiceFiscale(value);
  if (!CF_REGEX.test(cf)) return false;
  return computeCheckChar(cf.slice(0, 15)) === cf[15];
};

// Sostituisce le lettere di omocodia con le cifre originali
const decodeOmocodia = (cf) => {
  const chars = cf.split('');
  OMOCODIA_POSITIONS.forEach((pos) => {
    const omoIdx = OMOCODIA_LETTERS.indexOf(chars[pos]);
    if (omoIdx >= 0) chars[pos] = String(omoIdx);
  });
  return chars.join('');
};

/**
 * Ricava la data di nascita dal codice fiscale.
 * Restituisce una stringa YYYY-MM-DD oppure null se il CF non è valido.
 */
const getBirthDateFromCodiceFiscale = (value, today = new Date()) => {
  if (!isValidCodiceFiscale(value)) return null;
  const cf = decodeOmocodia(normalizeCodiceFiscale(value));

  const yy = parseInt(cf.slice(6, 8), 10);
  const month = MONTH_LETTERS.indexOf(cf[8]) + 1;
  let day = parseInt(cf.slice(9, 11), 10);
  if (day > 40) day -= 40;

  const currentYY = today.getFullYear() % 100;
  const year = yy <= currentYY ? 2000 + yy : 1900 + yy;

  const date = new Date(Date.UTC(year, month - 1, day));
  if (date.getUTCMonth() !== month - 1 || date.getUTCDate() !== day) {
    return null;
  }

  return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(
    2,
    '0'
  )}`;
};

const getAgeFromBirthDate = (birthDate, today = new Date()) => {
  const [year, month, day] = birthDate.split('-').map(Number);
  let age = today.getFullYear() - year;
  const m = today.getMonth() + 1 - month;
  if (m < 0 || (m === 0 && today.getDate() < day)) age -= 1;
  return age;
};

export {
  normalizeCodiceFiscale,
  isValidCodiceFiscale,
  getBirthDateFromCodiceFiscale,
  getAgeFromBirthDate,
};
